// pages/CalendarPage.js

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
// --- CORRECTED: We need getQuests to get the full details ---
import { getAiFeatureQuotas, getQuests, moveSwapQuest } from '../api/dataService';
import { useNavigate } from 'react-router-dom';

// --- Components and Icons ---
import QuestCard from '../components/QuestCard';
import VerificationModal from '../components/VerificationModal';
import MindSessionModal from '../components/MindSessionModal';
import SearchIcon from '../components/icons/SearchIcon';
import FilterIcon from '../components/icons/FilterIcon';
import ListIcon from '../components/icons/ListIcon';
import QuestDetailsModal from '../components/QuestDetailsModal';
import QuestMoveSwapModal from '../components/QuestMoveSwapModal';

// --- Icon Imports ---
import BicepIcon from '../components/icons/BicepIcon';
import MeditateIcon from '../components/icons/MeditateIcon';
import CutleryIcon from '../components/icons/CutleryIcon';
import { Brain } from 'lucide-react';
import { useCoachUsage } from '../context/CoachUsageContext';
import { useToast } from '../context/ToastContext';
import { VERIFICATION_MODAL_FEATURE_KEYS } from '../utils/aiFeatureQuotas';
import { detectBrowserTimezone } from '../utils/timezone';
import {
    applyOptimisticQuestMoveSwap,
    decorateCalendarQuest,
    getCurrentLocalWeekDays,
    getLocalDateKey,
    isQuestEligibleForMoveSwap,
} from '../utils/calendarMoveSwap';
import { useLocale } from '../context/LocaleContext';

const CalendarPage = () => {
    const { user } = useAuth();
    const { t, locale } = useLocale();
    const { addToast } = useToast() || {};
    const timezone = useMemo(() => detectBrowserTimezone() || 'UTC', []);
    const todayDateKey = useMemo(() => getLocalDateKey(new Date(), timezone), [timezone]);
    const [allQuests, setAllQuests] = useState([]);
    const [selectedDateKey, setSelectedDateKey] = useState(todayDateKey);
    const [verifyingQuest, setVerifyingQuest] = useState(null);
    const [activeMindQuest, setActiveMindQuest] = useState(null);
    const [verificationAiFeatureQuotas, setVerificationAiFeatureQuotas] = useState(null);
    const [viewingQuestDetails, setViewingQuestDetails] = useState(null); // Holds the quest object for the details modal
    const [movingQuest, setMovingQuest] = useState(null);
    const [moveSwapError, setMoveSwapError] = useState('');
    const [isMoveSwapSubmitting, setIsMoveSwapSubmitting] = useState(false);
    const [bannerMessage, setBannerMessage] = useState(null);
    const [viewMode, setViewMode] = useState('daily');
    const navigate = useNavigate();

    // UI State
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterType, setFilterType] = useState('all');
    const [days, setDays] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false); // NEW
    const pollingRef = useRef(null); // NEW
    const todayButtonRef = useRef(null);

    const questIcons = useMemo(() => ({
        'Workout': BicepIcon,
        'Mind': Brain,
        'Mindfulness': MeditateIcon,
        'Meal': CutleryIcon,
        'default': BicepIcon
    }), []);

    useCoachUsage();

    const fetchData = useCallback((isPolling = false) => {
        if (user) {
            const startFrom = new Date(Date.now() - (7 * 24 * 60 * 60 * 1000)).toISOString();

            getQuests({ startFrom }).then(questsRes => {
                const transformedQuests = questsRes.data
                    .filter(quest => !!quest.start)
                    .map((quest) => decorateCalendarQuest(quest, questIcons, timezone));

                console.log('[Calendar][Fetch]', {
                    locale,
                    timezone,
                    total: transformedQuests.length,
                    selectedDateKey,
                    questDates: transformedQuests.map((quest) => ({
                        id: quest.id,
                        name: quest.name || quest.canonicalName || '',
                        start: quest.start,
                        date: quest.date,
                    })),
                });

                if (isPolling && transformedQuests.length > 0) {
                    const hasNewTodayQuests = transformedQuests.some(q => q.date === todayDateKey);
                    if (hasNewTodayQuests) {
                        console.log('[CalendarPage] Polling successful. Stopping poll.');
                        setIsGenerating(false);
                        clearInterval(pollingRef.current);
                        pollingRef.current = null;
                    }
                }

                setAllQuests(transformedQuests);
            }).catch(err => {
                console.error(t('calendar.errors.load', 'Failed to fetch calendar data.'), err);
            });
        }
    }, [locale, questIcons, selectedDateKey, timezone, todayDateKey, user]);


    useEffect(() => {
        if (isGenerating) {
            if (pollingRef.current) clearInterval(pollingRef.current);
            pollingRef.current = setInterval(() => {
                console.log('[CalendarPage] Polling for new quests...');
                fetchData(true);
            }, 10000);
        }
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [isGenerating, fetchData]);

    useEffect(() => {
        let cancelled = false;
        getAiFeatureQuotas({ features: VERIFICATION_MODAL_FEATURE_KEYS })
            .then((response) => {
                if (!cancelled) {
                    setVerificationAiFeatureQuotas(response.data || null);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.warn('[CalendarPage] Failed to warm verification AI feature quotas', err);
                }
            });

        const handleQuotaRefresh = () => {
            getAiFeatureQuotas({ features: VERIFICATION_MODAL_FEATURE_KEYS, force: true })
                .then((response) => {
                    if (!cancelled) {
                        setVerificationAiFeatureQuotas(response.data || null);
                    }
                })
                .catch((err) => {
                    if (!cancelled) {
                        console.warn('[CalendarPage] Failed to refresh verification AI feature quotas', err);
                    }
                });
        };

        window.addEventListener('forza:aiFeatureQuotasRefresh', handleQuotaRefresh);
        return () => {
            cancelled = true;
            window.removeEventListener('forza:aiFeatureQuotasRefresh', handleQuotaRefresh);
        };
    }, []);


    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const localToday = new Date();
        localToday.setHours(0, 0, 0, 0);

        const generatedDays = Array.from({ length: 15 }, (_, i) => {
            const day = new Date(localToday);
            day.setDate(localToday.getDate() + (i - 7));

            return {
                date: day,
                dateString: getLocalDateKey(day, timezone),
                dayName: day.toLocaleDateString(locale, { weekday: 'short' }),
                dayNum: String(day.getDate()),
            };
        });
        setDays(generatedDays);
    }, [locale, timezone]);
    // Ensure today is scrolled into view on first render
    useEffect(() => {
        if (todayButtonRef.current) {
            todayButtonRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
    }, [days.length]);

    useEffect(() => {
        setSelectedDateKey((prev) => prev || todayDateKey);
    }, [todayDateKey]);

    useEffect(() => {
        if (!bannerMessage) {
            return undefined;
        }
        const timeoutId = setTimeout(() => setBannerMessage(null), 4000);
        return () => clearTimeout(timeoutId);
    }, [bannerMessage]);

    const filteredQuests = useMemo(() => {
        return allQuests.filter(quest => {
            const questName = String(quest?.name || quest?.canonicalName || '');
            const questType = String(quest?.type || '').toLowerCase();
            const matchSearch = questName.toLowerCase().includes(searchTerm.toLowerCase());
            const matchFilter = filterType === 'all' || questType === filterType;
            return matchSearch && matchFilter;
        });
    }, [allQuests, searchTerm, filterType]);

    const questsByDate = useMemo(() => {
        return filteredQuests.reduce((acc, quest) => {
            (acc[quest.date] = acc[quest.date] || []).push(quest);
            return acc;
        }, {});
    }, [filteredQuests]);

    useEffect(() => {
        const renderedDateKeys = Object.keys(questsByDate)
            .filter(Boolean)
            .sort((a, b) => new Date(`${a}T00:00:00Z`).getTime() - new Date(`${b}T00:00:00Z`).getTime());

        console.log('[Calendar][RenderedDates]', {
            selectedDateKey,
            todayDateKey,
            renderedDateKeys,
            filterType,
            searchTerm,
        });

        if (!renderedDateKeys.length) {
            return;
        }

        const selectedHasQuest = renderedDateKeys.includes(selectedDateKey);
        if (selectedHasQuest) {
            return;
        }

        const nextUpcomingDate = renderedDateKeys.find((dateKey) => dateKey >= todayDateKey) || renderedDateKeys[0];
        if (nextUpcomingDate && nextUpcomingDate !== selectedDateKey) {
            console.log('[Calendar][AutoSelectDate]', {
                previousSelectedDateKey: selectedDateKey,
                nextUpcomingDate,
            });
            setSelectedDateKey(nextUpcomingDate);
        }
    }, [filterType, questsByDate, searchTerm, selectedDateKey, todayDateKey]);

    const questsForSelectedDay = questsByDate[selectedDateKey] || [];
    const sortedDates = Object.keys(questsByDate).sort((a, b) => new Date(`${a}T00:00:00Z`).getTime() - new Date(`${b}T00:00:00Z`).getTime());
    const currentWeekDateKeys = useMemo(
        () => new Set(getCurrentLocalWeekDays({ timezone }).map((day) => day.dateKey)),
        [timezone]
    );

    const handleVerifyClick = (quest) => {
        if (quest?.type === 'Mind') {
            setActiveMindQuest(quest);
            return;
        }
        setVerifyingQuest(quest);
    };
    const handleViewDetails = (quest) => {
        setViewingQuestDetails(quest);
    };
    const handleMoveSwapClick = (quest) => {
        setMoveSwapError('');
        setMovingQuest(quest);
    };

    const handleCloseModal = () => {
        setVerifyingQuest(null);
    };

    const handleCloseMindModal = () => {
        setActiveMindQuest(null);
    };

    const handleCloseDetailsModal = () => {
        setViewingQuestDetails(null);
    };
    const handleCloseMoveSwapModal = () => {
        if (isMoveSwapSubmitting) {
            return;
        }
        setMoveSwapError('');
        setMovingQuest(null);
    };
    const handleVerificationSuccess = (payload) => {
        const leveledUp = typeof payload === 'object' ? payload?.leveledUp : payload;
        handleCloseModal(); // Close the verification modal first
        if (leveledUp) {
            console.log('[CalendarPage] Level up detected! Redirecting to dashboard...');
            // Use a timeout to allow the user to read the success message before redirecting
            setTimeout(() => {
                navigate('/dashboard', { state: { isGenerating: true } });
            }, 1500); // 1.5 second delay
        } else {
            fetchData(); // If no level up, just refresh the calendar data
        }
    };

    const handleMindSessionSuccess = (payload) => {
        const leveledUp = typeof payload === 'object' ? payload?.leveledUp : payload;
        handleCloseMindModal();
        if (leveledUp) {
            setTimeout(() => {
                navigate('/dashboard', { state: { isGenerating: true } });
            }, 1500);
            return;
        }
        fetchData();
    };

    const selectedDateLabel = useMemo(() => (
        new Date(`${selectedDateKey}T00:00:00`).toLocaleDateString(locale, { month: 'long', day: 'numeric' })
    ), [locale, selectedDateKey]);

    const describeMoveSwapSuccess = useCallback((quest, option) => {
        if (!option) {
            return t('calendar.errors.updated', 'Quest updated.');
        }

        if (option.action === 'move') {
            return t('calendar.errors.movedTo', '{quest} moved to {target}.', {
                quest: quest.name,
                target: option.shortLabel,
            });
        }

        return t('calendar.errors.swappedWith', '{quest} swapped with {target}.', {
            quest: quest.name,
            target: option.shortLabel,
        });
    }, [t]);

    const handleMoveSwapConfirm = useCallback(async (option) => {
        if (!movingQuest || !option) {
            return;
        }

        const sourceQuestId = movingQuest._id || movingQuest.id;
        const previousQuests = allQuests;
        const optimisticQuests = applyOptimisticQuestMoveSwap({
            quests: allQuests,
            sourceQuestId,
            option,
            timezone,
        });

        setMoveSwapError('');
        setIsMoveSwapSubmitting(true);
        setAllQuests(optimisticQuests);

        try {
            await moveSwapQuest({
                questId: sourceQuestId,
                targetDate: option.dateKey,
            });
            const successMessage = describeMoveSwapSuccess(movingQuest, option);
            setBannerMessage({ type: 'success', message: successMessage });
            addToast?.(successMessage, 'success');
            setMovingQuest(null);
            await fetchData();
        } catch (error) {
            const message = error?.response?.data?.message || t('calendar.errors.updateQuest', 'Unable to update this quest right now.');
            setAllQuests(previousQuests);
            setMoveSwapError(message);
            setBannerMessage({ type: 'error', message });
            addToast?.(message, 'error');
        } finally {
            setIsMoveSwapSubmitting(false);
        }
    }, [addToast, allQuests, describeMoveSwapSuccess, fetchData, movingQuest, timezone]);

    return (
        <section className="container mx-auto px-4 max-w-md">
            {/* ... Header and controls ... */}
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-white my-4">{t('calendar.title', 'Calendar')}</h1>
                <div className="flex items-center space-x-2">
                    <button aria-label={t('calendar.toggleSearch', 'Toggle search')} onClick={() => setIsSearchOpen(!isSearchOpen)} className="p-2 bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-xl hover:bg-slate-800/50 transition-colors shadow-sm"><SearchIcon className="w-5 h-5 text-cyan-400" /></button>
                    <div className="relative">
                        <button aria-label={t('calendar.toggleFilter', 'Toggle filter')} onClick={() => setIsFilterOpen(!isFilterOpen)} className="p-2 bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-xl hover:bg-slate-800/50 transition-colors shadow-sm"><FilterIcon className="w-5 h-5 text-cyan-400" /></button>
                        {isFilterOpen && (
                            <div className="absolute top-full right-0 mt-2 w-40 bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-[1rem] shadow-2xl z-20 overflow-hidden">
                                {['all', 'workout', 'mind', 'mindfulness', 'meal'].map(type => (
                                    <button key={type} onClick={() => { setFilterType(type); setIsFilterOpen(false); }} className={`w-full text-left px-4 py-2.5 text-sm capitalize transition-colors ${filterType === type ? 'bg-cyan-900/40 text-cyan-400' : 'text-gray-300'} hover:bg-slate-800`}><span className="relative z-10">{t(`calendar.filters.${type}`, type)}</span></button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button aria-label={t('calendar.toggleView', 'Toggle calendar view')} onClick={() => setViewMode(viewMode === 'daily' ? 'list' : 'daily')} className="p-2 bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-xl hover:bg-slate-800/50 transition-colors shadow-sm"><ListIcon className="w-5 h-5 text-cyan-400" /></button>
                </div>
            </div>

            {bannerMessage ? (
                <div
                    className={`mb-4 rounded-2xl border px-4 py-3 text-sm font-semibold ${
                        bannerMessage.type === 'error'
                            ? 'border-red-500/30 bg-red-950/40 text-red-100'
                            : 'border-emerald-500/30 bg-emerald-950/40 text-emerald-100'
                    }`}
                >
                    {bannerMessage.message}
                </div>
            ) : null}

            {isSearchOpen && (
                <div className="mb-4">
                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={t('calendar.searchPlaceholder', 'Search quests...')} className="w-full bg-[#1e2022] rounded-lg p-3 text-gray-300 placeholder-gray-500 border border-gray-600 focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400" />
                </div>
            )}

            {viewMode === 'daily' && (
                <div className="flex overflow-x-auto space-x-3 pb-3 mb-6 scrollbar-hide">
                    {days.map(({ dateString, dayName, dayNum }) => (
                        <button
                            key={dateString}
                            ref={dateString === todayDateKey ? todayButtonRef : null}
                            onClick={() => setSelectedDateKey(dateString)}
                            className={`flex-shrink-0 flex flex-col items-center justify-center w-14 h-20 rounded-[1rem] transition-all duration-200 border ${selectedDateKey === dateString ? 'bg-gradient-to-br from-cyan-400 to-blue-500 text-gray-900 border-cyan-300/50 shadow-lg shadow-cyan-500/25' : 'bg-slate-950/50 border-white/5 text-gray-300 hover:bg-slate-800/50 hover:border-white/10 shadow-inner'}`}>
                            <span className="text-xs font-bold uppercase tracking-wider">{dayName}</span>
                            <span className="text-2xl font-bold">{dayNum}</span>
                        </button>
                    ))}
                </div>
            )}

            {viewMode === 'daily' ? (
                <div>
                    <h3 className="text-lg font-semibold text-gray-300 mb-4">
                        {t('calendar.questsForDay', 'Quests for {date}', { date: selectedDateLabel })}
                    </h3>
                    {isGenerating ? (
                        <div className="bg-[#2C2F33] rounded-xl p-6 text-center text-gray-400">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-4"></div>
                            <p className="font-semibold">{t('calendar.generatingAfterLevelUp', 'Generating new quests after level-up...')}</p>
                        </div>
                    ) : questsForSelectedDay.length > 0 ? (
                        <div className="space-y-4">
                            {questsForSelectedDay.map(quest => (
                                <QuestCard
                                    key={quest.id}
                                    quest={quest}
                                    onViewDetails={handleViewDetails}
                                    handleVerifyClick={handleVerifyClick}
                                    handleMoveSwapClick={handleMoveSwapClick}
                                    canMoveSwap={isQuestEligibleForMoveSwap({ quest, timezone }) && currentWeekDateKeys.has(quest.date)}
                                    isToday={quest.date === todayDateKey}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-8">{t('calendar.noQuestsForDay', 'No quests scheduled for this day.')}</p>
                    )}
                </div>
            ) : (
                <div>
                    <h3 className="text-lg font-semibold text-gray-300 mb-4">{t('calendar.allScheduledQuests', 'All Scheduled Quests')}</h3>
                    {sortedDates.length > 0 ? (
                        <div className="space-y-6">
                            {sortedDates.map(dateStr => (
                                <div key={dateStr}>
                                    <h4 className="font-bold text-cyan-400 mb-2">{new Date(dateStr + 'T00:00:00Z').toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' })}</h4>
                                    <div className="space-y-4">
                                        {questsByDate[dateStr].map(quest => (
                                            <QuestCard
                                                key={quest.id}
                                                quest={quest}
                                                onViewDetails={handleViewDetails}
                                                handleVerifyClick={handleVerifyClick}
                                                handleMoveSwapClick={handleMoveSwapClick}
                                                canMoveSwap={isQuestEligibleForMoveSwap({ quest, timezone }) && currentWeekDateKeys.has(quest.date)}
                                                isToday={quest.date === todayDateKey}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-8">{t('calendar.noQuestsFound', 'No quests found.')}</p>
                    )}
                </div>
            )}

            {/* Render Verification Modal */}
            {verifyingQuest && (
                <VerificationModal
                    quest={verifyingQuest}
                    allQuests={allQuests}
                    initialAiFeatureQuotas={verificationAiFeatureQuotas}
                    onClose={handleCloseModal}
                    onVerificationSuccess={handleVerificationSuccess}
                />
            )}

            {activeMindQuest && (
                <MindSessionModal
                    isOpen={!!activeMindQuest}
                    quest={activeMindQuest}
                    onClose={handleCloseMindModal}
                    onComplete={handleMindSessionSuccess}
                />
            )}

            {/* Render Quest Details Modal */}
            {viewingQuestDetails && (
                <QuestDetailsModal
                    quest={viewingQuestDetails}
                    isOpen={!!viewingQuestDetails}
                    onClose={handleCloseDetailsModal}
                />
            )}

            {movingQuest && (
                <QuestMoveSwapModal
                    isOpen={!!movingQuest}
                    onClose={handleCloseMoveSwapModal}
                    onConfirm={handleMoveSwapConfirm}
                    quest={movingQuest}
                    allQuests={allQuests}
                    timezone={timezone}
                    errorMessage={moveSwapError}
                    isSubmitting={isMoveSwapSubmitting}
                />
            )}
            <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; } .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
        </section>
    );
};

export default CalendarPage;
